import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, ChevronUp, Play, Pause, AlertTriangle } from "lucide-react";
import useTrackEventStream from "../../../components/hook/use-track-event-stream";
import { MERIDIAN_TRACK_WRITE_KEY, useTrackRecentEvents, useTrackStreamStats } from "../../../components/hook/use-track-collect";
import { useTrackLiveAccess } from "../../../components/hook/use-track-live-access";

// Everything on this page comes off the real backend, per the backend
// team's write-up (2026-09-23): the live SSE (GET .../stream/events, an
// EventSource — use-track-event-stream.ts) is a push feed that only sends
// events from the moment it's connected, so on its own the panel looked
// empty even though events were really arriving. Two more real endpoints
// (use-track-collect.ts) fill the gaps: GET .../stream/recent backfills on
// load and on every reconnect, and GET .../stream/stats drives the metric
// cards instead of a client-side estimate. The stream never "holds"
// history — this page's own `events` state is what makes rows persist
// rather than flashing and vanishing. The write key is one tenant's
// (Meridian), so in production builds the stream only opens for that org
// (see use-track-live-access.ts).

const MAX_REAL_EVENTS = 200;

interface DeliveryTarget {
  name: string;
  status: "Delivered" | "Failed";
  latencyMs: number;
}

interface StreamEvent {
  id: string;
  timestampMs: number;
  eventName: string;
  sourceLabel: string;
  identity: string;
  status: string;
  anonymousId: string;
  sdkSource: string;
  path: string;
  referrer: string;
  sentAt: string;
  requestId: string;
  deliveries: DeliveryTarget[];
  // The payload exactly as received — what the expanded row shows.
  raw?: unknown;
}


const pickStr = (...vals: unknown[]): string => {
  for (const v of vals) if (typeof v === "string" && v) return v;
  return "";
};

const toMs = (v: unknown): number => {
  if (typeof v === "number") return v < 1e12 ? v * 1000 : v;
  if (typeof v === "string") {
    const t = Date.parse(v);
    if (!Number.isNaN(t)) return t;
  }
  return Date.now();
};

const HEARTBEAT_TYPES = new Set(["ping", "heartbeat", "keepalive", "connected", "ready"]);

// String/object → a short display label (source and identity may arrive as
// either).
// `identity` is confirmed as `{ type, value }` — `value` has to be checked
// before `type`, or an identity like `{ type: "anonymous_id", value: "..." }`
// would display its type ("anonymous_id") instead of the actual value.
const describe = (v: unknown): string => {
  if (typeof v === "string") return v;
  if (v && typeof v === "object") {
    const o = v as Record<string, any>;
    return pickStr(o.value, o.name, o.user_id, o.anonymous_id, o.id, o.email, o.type) || JSON.stringify(v);
  }
  return "";
};

// Maps one stream message — { event_id, time, occurred_at, event_type,
// event_name, source, identity, status, utm_*, click_id, properties,
// traits } — onto a row. The untouched payload is kept in `raw` and is what
// the expanded row shows. Returns null for non-events (heartbeats, plain
// strings).
const normalizeEvent = (raw: unknown, n: number): StreamEvent | null => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const e = raw as Record<string, any>;
  if (HEARTBEAT_TYPES.has(pickStr(e.type, e.event_type).toLowerCase())) return null;
  const props: Record<string, any> = e.properties && typeof e.properties === "object" ? e.properties : {};
  const traits: Record<string, any> = e.traits && typeof e.traits === "object" ? e.traits : {};

  const ts = toMs(e.occurred_at ?? e.time ?? e.timestamp);
  const identity = describe(e.identity) || pickStr(traits.user_id, traits.email);
  return {
    id: pickStr(e.event_id, e.message_id, e.id) || `evt-${n}-${ts}`,
    timestampMs: ts,
    eventName: pickStr(e.event_name, e.event, e.name, e.event_type) || "Unnamed event",
    sourceLabel: describe(e.source) || "—",
    identity: identity || "—",
    status: pickStr(e.status) || "Received",
    anonymousId: pickStr(e.anonymous_id, traits.anonymous_id),
    sdkSource: describe(e.source),
    path: pickStr(props.path, props.url),
    referrer: pickStr(props.referrer),
    sentAt: new Date(ts).toISOString(),
    requestId: pickStr(e.event_id, e.request_id),
    deliveries: Array.isArray(e.deliveries)
      ? e.deliveries.map((d: any) => ({ name: pickStr(d?.name, d?.destination), status: /fail|error/i.test(pickStr(d?.status)) ? "Failed" : "Delivered", latencyMs: Number(d?.latency_ms ?? d?.latencyMs ?? 0) }))
      : [],
    raw,
  };
};

// Real statuses aren't a fixed set, so color by meaning instead of by exact
// name; the simulated ones still match STATUS_STYLES directly.
const statusStyle = (status: string): string => {
  if (STATUS_STYLES[status]) return STATUS_STYLES[status];
  if (/fail|error|reject|drop|invalid/i.test(status)) return STATUS_STYLES["Not Interested"];
  if (/deliver|success|ok|processed|received|accepted/i.test(status)) return STATUS_STYLES.Delivered;
  return "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300";
};



const formatTime = (ms: number): string => new Date(ms).toISOString().slice(11, 23);


const STATUS_STYLES: Record<string, string> = {
  Delivered: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  Received: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  "Not Interested": "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400",
  "Schema drift": "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
};


const Dropdown: React.FC<{ label: string; value?: string; options: string[]; onChange?: (v: string) => void }> = ({
  label,
  value: controlledValue,
  options,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(label);
  const value = controlledValue ?? internalValue;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap"
      >
        {value}
        <ChevronDown size={14} className={`text-gray-400 dark:text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 min-w-full w-max rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg overflow-hidden">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => {
                setInternalValue(opt);
                setOpen(false);
                onChange?.(opt);
              }}
              className={`block w-full text-left px-3.5 py-2 text-sm whitespace-nowrap hover:bg-gray-50 dark:hover:bg-gray-800 ${
                opt === value ? "text-primary-600 dark:text-primary-400 font-semibold" : "text-gray-700 dark:text-gray-200"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


const EventRow: React.FC<{ event: StreamEvent }> = ({ event }) => {
  const [expanded, setExpanded] = useState(false);

  const payload = useMemo(
    () =>
      JSON.stringify(
        event.raw,
        null,
        2
      ),
    [event]
  );

  return (
    <>
      <tr
        onClick={() => setExpanded((s) => !s)}
        className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50/60 dark:hover:bg-gray-800/40 transition-colors cursor-pointer"
      >
        <td className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap font-mono">
          <span className="flex items-center gap-2">
            {formatTime(event.timestampMs)}
          </span>
        </td>
        <td
          className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
            event.status === "Schema drift" ? "text-amber-600 dark:text-amber-400 font-mono" : "text-gray-800 dark:text-gray-100"
          }`}
        >
          {event.eventName}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">{event.sourceLabel}</td>
        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">{event.identity}</td>
        <td className="px-4 py-3 whitespace-nowrap">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle(event.status)}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current" />
            {event.status}
          </span>
        </td>
        <td className="px-4 py-3 text-right">
          {expanded ? (
            <ChevronUp size={16} className="text-gray-400 dark:text-gray-500 inline-block" />
          ) : (
            <ChevronDown size={16} className="text-gray-400 dark:text-gray-500 inline-block" />
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-gray-50 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-950/40">
          <td colSpan={6} className="px-4 py-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">Raw payload</p>
                <pre className="text-xs font-mono text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words">
                  {payload}
                </pre>
              </div>
            </div>
            {event.deliveries.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">Delivered to</p>
              <div className="space-y-1.5">
                {event.deliveries.map((d) => (
                  <div
                    key={d.name}
                    className="flex items-center justify-between gap-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2"
                  >
                    <span className="text-sm text-gray-700 dark:text-gray-200">{d.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">
                        {d.status}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">{d.latencyMs}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};


const NO_EVENTS: StreamEvent[] = [];

const LiveEventStreamPage: React.FC = () => {
  const { allowed: hasLiveAccess, checked: accessChecked, orgName } = useTrackLiveAccess();
  // "Test" is a separate environment (a pk_test_ key) that isn't connected
  // yet, so that tab is deliberately empty rather than showing invented rows.
  const [dataMode, setDataMode] = useState<"test" | "live">("live");
  const [live, setLive] = useState(true); // the stream opens on load; pause closes it
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("All sources");
  const [statusFilter, setStatusFilter] = useState("All status");

  const streaming = hasLiveAccess && dataMode === "live" && live;
  const [events, setEvents] = useState<StreamEvent[]>([]);
  const seqRef = useRef(0);
  const { status: streamStatus } = useTrackEventStream(streaming, MERIDIAN_TRACK_WRITE_KEY, (raw) => {
    seqRef.current += 1;
    const ev = normalizeEvent(raw, seqRef.current);
    if (!ev) return;
    // Reconnects can replay recent events — keep each id once.
    setEvents((prev) => (prev.some((p) => p.id === ev.id) ? prev : [ev, ...prev].slice(0, MAX_REAL_EVENTS)));
  });

  // The SSE stream only pushes events from the moment it's connected —
  // GET /stream/recent backs it up with everything that fired before that
  // (or during a dropped-connection gap). `connectCount` bumps on the
  // initial connect and every reconnect, so this re-runs each time.
  const [connectCount, setConnectCount] = useState(0);
  const prevStreamStatusRef = useRef<string>("idle");
  useEffect(() => {
    if (streamStatus === "open" && prevStreamStatusRef.current !== "open") {
      setConnectCount((c) => c + 1);
    }
    prevStreamStatusRef.current = streamStatus;
  }, [streamStatus]);

  // /stream/recent is a rolling recent buffer (no offset/cursor, so no real
  // pagination) — pull the largest window (200, matching MAX_REAL_EVENTS)
  // rather than paginating.
  const { data: recentRaw } = useTrackRecentEvents(MERIDIAN_TRACK_WRITE_KEY, MAX_REAL_EVENTS, connectCount);
  useEffect(() => {
    if (!recentRaw.length) return;
    setEvents((prev) => {
      const known = new Set(prev.map((p) => p.id));
      const additions: StreamEvent[] = [];
      recentRaw.forEach((raw, i) => {
        const ev = normalizeEvent(raw, -(i + 1));
        if (ev && !known.has(ev.id)) additions.push(ev);
      });
      if (!additions.length) return prev;
      return [...prev, ...additions].sort((a, b) => b.timestampMs - a.timestampMs).slice(0, MAX_REAL_EVENTS);
    });
  }, [recentRaw]);

  // Real aggregate stats (GET /stream/stats) drive the metric cards instead
  // of a client-side estimate over whatever's currently in `events`.
  const stats = useTrackStreamStats(streaming, MERIDIAN_TRACK_WRITE_KEY, 5000);

  const visibleEvents = dataMode === "live" ? events : NO_EVENTS;

  const sourceOptions = useMemo(() => ["All sources", ...Array.from(new Set(events.map((e) => e.sourceLabel)))], [events]);
  const statusOptions = useMemo(() => ["All status", ...Array.from(new Set(events.map((e) => e.status)))], [events]);

  const filtered = useMemo(() => {
    let list = visibleEvents;
    if (sourceFilter !== "All sources") list = list.filter((e) => e.sourceLabel === sourceFilter);
    if (statusFilter !== "All status") list = list.filter((e) => e.status === statusFilter);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((e) => e.eventName.toLowerCase().includes(q) || e.identity.toLowerCase().includes(q));
    return list;
  }, [visibleEvents, search, sourceFilter, statusFilter]);

  const metrics = useMemo(
    () => [
      { key: "eps", label: "Events / sec", value: stats ? stats.events_per_second.toFixed(1) : "—" },
      { key: "hour", label: "Events, last hour", value: stats ? String(stats.events_last_hour) : "—" },
      { key: "total", label: "Total events", value: stats ? String(stats.total) : "—" },
      { key: "sources", label: "Active sources", value: stats ? String(stats.active_sources) : "—" },
    ],
    [stats]
  );

  const badge =
    streaming && streamStatus === "open"
      ? { text: "Live", cls: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400", dot: "bg-emerald-500 animate-pulse" }
      : streaming && streamStatus === "error"
        ? { text: "Disconnected", cls: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400", dot: "bg-rose-500" }
        : streaming
          ? { text: "Connecting…", cls: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400", dot: "bg-amber-500 animate-pulse" }
          : { text: "Paused", cls: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400", dot: "bg-gray-400 dark:bg-gray-500" };

  const emptyMessage =
    dataMode === "test"
      ? "The test environment isn't connected yet — no test events to show."
      : accessChecked && !hasLiveAccess
        ? `The real-time stream is only enabled for the Meridian tenant${orgName ? ` (you're signed in as "${orgName}")` : ""}.`
        : !live
          ? "Stream paused — press play to start listening."
          : streamStatus === "error"
            ? "Couldn't reach the event stream. Press pause, then play to try again."
            : streamStatus === "open"
              ? "Listening — events fired from your site will appear here in real time."
              : "Connecting to the event stream…";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">Live event stream</h1>
            <span className="text-sm text-gray-400 dark:text-gray-500">{sourceFilter} · real-time</span>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden p-0.5 bg-gray-50 dark:bg-gray-900">
              {(["test", "live"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setDataMode(m)}
                  className={`px-3.5 py-1.5 text-sm font-medium rounded-lg transition-colors capitalize ${
                    dataMode === m ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-sm" : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>

            <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${badge.cls}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
              {badge.text}
            </span>

            <button
              type="button"
              onClick={() => setLive((s) => !s)}
              title={live ? "Pause" : "Resume"}
              className="flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              {live ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
            </button>
          </div>
        </div>

        {dataMode === "live" && accessChecked && !hasLiveAccess && (
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/20 text-sm text-amber-800 dark:text-amber-300">
            <AlertTriangle size={14} className="shrink-0" />
            {emptyMessage}
          </div>
        )}

        {/* Metric cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {metrics.map((m) => (
            <div key={m.key} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-2 truncate">{m.label}</p>
              <p className="text-2xl sm:text-3xl font-semibold text-primary-600 dark:text-primary-400">{m.value}</p>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search events"
              className="w-full pl-9 pr-3 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-500/20"
            />
          </div>
          <Dropdown label="All sources" value={sourceFilter} options={sourceOptions} onChange={setSourceFilter} />
          <Dropdown label="All status" value={statusFilter} options={statusOptions} onChange={setStatusFilter} />
          <span className="text-sm text-gray-400 dark:text-gray-500 whitespace-nowrap">{filtered.length} rows</span>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Time</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Event Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Source</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Identity</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-sm text-gray-400 dark:text-gray-500">
                      {visibleEvents.length === 0 ? emptyMessage : "No events match these filters."}
                    </td>
                  </tr>
                ) : (
                  filtered.map((e) => <EventRow key={e.id} event={e} />)
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveEventStreamPage;
